import { useMemo } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faCalculatorSimple } from '@fortawesome/pro-duotone-svg-icons';
import { faSquare1, faStarOfLife } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	Box,
	TableBody,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material';

import { calculationQueries } from '@repo/api/claim/calculations';
import { isCalculatedExpenses } from '@repo/api/claim/calculations.types';
import { claimQueries } from '@repo/api/claim/claims';
import { timeNames, timePeriodNames } from '@repo/api/common.types';
import { countryQueries } from '@repo/api/countries';
import { formatMonthYear } from '@repo/api/shared/calculations';
import useCurrency from '@repo/features/hooks/useCurrency';

import { CalculationTable } from './-components/CalculationTable';
import { Cell } from './-components/Cell';
import { CurrencyCell } from './-components/CurrencyCell';
import { Footnote } from './-components/Footnote';
import { HasMi, HasMi4Or5 } from './-components/HasMi';

const sectionOrder = [
	'Sales',
	'OtherIncome',
	'CostOfGoodsSold',
	'Labour',
	'OtherCosts',
	'Expenses',
];

const crossSectionFootnoteCategories = ['payroll'];

const getFootnoteLetter = (index: number) => String.fromCharCode(65 + index);

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/expenses',
)({
	component: function CalculationsExpensesPage() {
		const { claimId } = Route.useParams();

		const [{ data: claim }, { data }, { data: countries }] = useSuspenseQueries(
			{
				queries: [
					claimQueries.id(claimId),
					calculationQueries.expenses(claimId),
					countryQueries.list(),
				],
			},
		);

		const currency = useCurrency(claim.claimInsured.countryId);

		const countryName = useMemo(
			() => countries.find((c) => c.id === claim.claimInsured.countryId)?.name,
			[countries, claim.claimInsured.countryId],
		);

		const { categorizationText, lowerCategorizationText } = useMemo(
			() => ({
				categorizationText:
					countryName === 'United Kingdom' ?
						'MDDi Categorisation'
					:	'MDDi Categorization',
				lowerCategorizationText:
					countryName === 'United Kingdom' ?
						'MDDi categorisation'
					:	'MDDi categorization',
			}),
			[countryName],
		);

		const theme = useTheme();

		const expenseViewModel = useMemo(() => {
			if (!isCalculatedExpenses(data)) {
				return {
					sales: [],
					otherIncome: [],
					costOfGoodsSold: [],
					labour: [],
					otherCosts: [],
					expenses: [],
					footnotes: [],
					hasMiValuePerformed: false,
					hasMiCategoryPerformed: false,
					hasMiType4Or5Performed: false,
				};
			}

			type Footnote = (typeof data.footnotes)[number];
			type Expense = (typeof data.expenses)[number] & {
				footnoteLetter?: string;
			};

			return data.expenses
				.toSorted(
					(a, b) =>
						sectionOrder.indexOf(a.expenseSection) -
						sectionOrder.indexOf(b.expenseSection),
				)
				.reduce<{
					sales: Expense[];
					otherIncome: Expense[];
					costOfGoodsSold: Expense[];
					labour: Expense[];
					otherCosts: Expense[];
					expenses: Expense[];
					footnotes: Footnote[];
					hasMiValuePerformed: boolean;
					hasMiCategoryPerformed: boolean;
					hasMiType4Or5Performed: boolean;
				}>(
					(accumulator, expense: Expense) => {
						const matchFootnote = (footnote: {
							note: string;
							expenseCategory: string;
							expenseSection: string;
						}): boolean =>
							footnote.expenseCategory === expense.expenseCategory &&
							(footnote.expenseSection === expense.expenseSection ||
								crossSectionFootnoteCategories.includes(
									footnote.expenseCategory,
								));

						const footnote = data.footnotes.find(matchFootnote);

						if (footnote) {
							const footnoteIndex =
								accumulator.footnotes.findIndex(matchFootnote);

							expense.footnoteLetter =
								footnoteIndex === -1 ?
									getFootnoteLetter(accumulator.footnotes.push(footnote) - 1)
								:	getFootnoteLetter(footnoteIndex);
						}

						switch (expense.expenseSection) {
							case 'Sales':
								accumulator.sales.push(expense);
								break;
							case 'OtherIncome':
								accumulator.otherIncome.push(expense);
								break;
							case 'CostOfGoodsSold':
								accumulator.costOfGoodsSold.push(expense);
								break;
							case 'Labour':
								accumulator.labour.push(expense);
								break;
							case 'OtherCosts':
								accumulator.otherCosts.push(expense);
								break;
							case 'Expenses':
								accumulator.expenses.push(expense);
								break;
						}

						if (expense.manualInterventionPerformed === 1) {
							accumulator.hasMiValuePerformed = true;
						}
						if (expense.manualInterventionPerformed === 2) {
							accumulator.hasMiCategoryPerformed = true;
						}
						if (expense.manualInterventionPerformed === 3) {
							accumulator.hasMiValuePerformed = true;
							accumulator.hasMiCategoryPerformed = true;
						}
						if (
							expense.manualInterventionPerformed === 4 ||
							expense.manualInterventionPerformed === 5
						) {
							accumulator.hasMiType4Or5Performed = true;
						}

						return accumulator;
					},
					{
						sales: [],
						otherIncome: [],
						costOfGoodsSold: [],
						labour: [],
						otherCosts: [],
						expenses: [],
						footnotes: [],
						hasMiValuePerformed: false,
						hasMiCategoryPerformed: false,
						hasMiType4Or5Performed: false,
					},
				);
		}, [data]);

		if (!isCalculatedExpenses(data)) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						p: 4,
						gap: 1,
						border: '1px solid',
						borderColor: 'accent.main',
						width: '600px',
						maxWidth: '100%',
						mx: 'auto',
					}}
				>
					<FontAwesomeIcon icon={faCalculatorSimple} size="2xl" />
					<Typography variant="subtitle1">No Calculations Data</Typography>
				</Box>
			);
		}

		const {
			sales,
			otherIncome,
			costOfGoodsSold,
			labour,
			otherCosts,
			expenses,
			footnotes,
			hasMiValuePerformed,
			hasMiCategoryPerformed,
			hasMiType4Or5Performed,
		} = expenseViewModel;

		const start = formatMonthYear(data.startDate);
		const end = formatMonthYear(data.endDate);

		return (
			<Box
				sx={{
					overflow: 'auto hidden',
				}}
			>
				<CalculationTable>
					<TableHead>
						<TableRow>
							<Cell header rowSpan={2}>
								Per Insured's Documentation
							</Cell>
							<Cell header rowSpan={2}>
								{categorizationText}
							</Cell>
							<Cell header colSpan={2}>
								{start} - {end}
							</Cell>
							<Cell
								header
								rowSpan={2}
								sx={{ '& span': { whiteSpace: 'nowrap' } }}
							>
								<span>
									{timePeriodNames[data.indemnityPeriodType]}
									<br />
									Average /
								</span>
								<br />
								<span>Saved Cost %</span>
							</Cell>
							<Cell header colSpan={2}>
								BI Analysis
							</Cell>
						</TableRow>
						<TableRow>
							<Cell header>Amount</Cell>
							<Cell header>Percent of Sales</Cell>
							<Cell header>Description</Cell>
							<Cell header>Amount</Cell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<Cell />
							<Cell />
							<Cell header small>
								({currency.display ?? currency.currency})
							</Cell>
							<Cell />
							<Cell />
							<Cell />
							<Cell header small>
								({currency.display ?? currency.currency})
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow>
							<Cell header align="left">
								Sales
							</Cell>
						</TableRow>
						{sales.map((s, i, a) => (
							<TableRow
								key={`${s.expenseSection}:${s.expenseCategory}:${s.expenseDescription}`}
								className="striped"
							>
								<Cell indent>{s.expenseDescription}</Cell>
								<Cell>
									{s.mddGrouping}
									{(s.manualInterventionPerformed === 2 ||
										s.manualInterventionPerformed === 3) && (
										<HasMi lowerCategorizationText={lowerCategorizationText} />
									)}{' '}
									{(s.manualInterventionPerformed === 4 ||
										s.manualInterventionPerformed === 5) && (
										<HasMi4Or5
											lowerCategorizationText={lowerCategorizationText}
										/>
									)}{' '}
								</Cell>
								<CurrencyCell
									{...currency}
									underline={i === a.length - 1}
									hideSymbol={i !== 0}
									highlight={
										s.manualInterventionPerformed === 1 ||
										s.manualInterventionPerformed === 5 ||
										s.manualInterventionPerformed === 3
									}
								>
									{s.amount}
								</CurrencyCell>
								<Cell percent underline={i === a.length - 1}>
									{s.percent}
								</Cell>
								<Cell />
								<Cell />
								<Cell />
							</TableRow>
						))}
						<TableRow>
							<Cell>Total Sales</Cell>
							<Cell />
							<CurrencyCell
								{...currency}
								hideSymbol={sales.length > 0}
								highlight={
									!!data.salesAmountManualIntervention &&
									data.salesAmountManualIntervention === 1
								}
							>
								{data.salesAmount}
							</CurrencyCell>
							<Cell percent>{data.salesPercent}</Cell>
							<Cell />
							<Cell>Daily Average</Cell>
							<CurrencyCell {...currency}>{data.dailyAverage}</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell />
							<Cell />
							<Cell />
							<Cell />
							<Cell />
							<Cell nowrap>
								Indemnity Period - {timeNames[data.indemnityPeriodType]}
							</Cell>
							<Cell number>{data.indemnityPeriod}</Cell>
						</TableRow>
						<TableRow>
							<Cell header align="left">
								{otherIncome.length > 0 ? 'Other Income' : ''}
							</Cell>
							<Cell />
							<Cell />
							<Cell />
							<Cell />
							<Cell>
								Waiting Period
								{claim.claimPolicy?.waitingPeriod ?
									claim.claimPolicy.waitingPeriodDurationHours ?
										' - Hours'
									:	' - Days'
								:	''}
							</Cell>
							<Cell number align="right">
								{claim.claimPolicy?.waitingPeriodDuration ?? 0}
							</Cell>
						</TableRow>
						<TableRow className={otherIncome[0] ? 'striped' : ''}>
							<Cell indent>{otherIncome[0]?.expenseDescription}</Cell>
							<Cell>
								{otherIncome[0]?.mddGrouping}
								{(otherIncome[0]?.manualInterventionPerformed === 2 ||
									otherIncome[0]?.manualInterventionPerformed === 3) && (
									<HasMi lowerCategorizationText={lowerCategorizationText} />
								)}{' '}
								{(otherIncome[0]?.manualInterventionPerformed === 4 ||
									otherIncome[0]?.manualInterventionPerformed === 5) && (
									<HasMi4Or5
										lowerCategorizationText={lowerCategorizationText}
									/>
								)}{' '}
							</Cell>
							<CurrencyCell
								{...currency}
								hideSymbol
								underline={otherIncome.length === 1}
								highlight={
									otherIncome[0]?.manualInterventionPerformed === 1 ||
									otherIncome[0]?.manualInterventionPerformed === 5 ||
									otherIncome[0]?.manualInterventionPerformed === 3
								}
							>
								{otherIncome[0]?.amount}
							</CurrencyCell>
							<Cell percent underline={otherIncome.length === 1}>
								{otherIncome[0]?.percent}
							</Cell>
							<Cell />
							<Cell
								sx={{
									backgroundColor: 'white',
								}}
							>
								Sales Loss
							</Cell>
							<CurrencyCell
								{...currency}
								underline
								overline
								sx={{
									backgroundColor: 'white',
								}}
							>
								{data.salesBiLoss}
							</CurrencyCell>
						</TableRow>
						{otherIncome.length > 1 &&
							otherIncome.slice(1).map((o, i, a) => (
								<TableRow
									key={`${o.expenseSection}:${o.expenseCategory}:${o.expenseDescription}`}
									className="striped"
								>
									<Cell indent>{o.expenseDescription}</Cell>
									<Cell>
										{o.mddGrouping}
										{(o.manualInterventionPerformed === 2 ||
											o.manualInterventionPerformed === 3) && (
											<HasMi
												lowerCategorizationText={lowerCategorizationText}
											/>
										)}{' '}
										{(o.manualInterventionPerformed === 4 ||
											o.manualInterventionPerformed === 5) && (
											<HasMi4Or5
												lowerCategorizationText={lowerCategorizationText}
											/>
										)}{' '}
									</Cell>
									<CurrencyCell
										{...currency}
										hideSymbol
										underline={i === a.length - 1}
										highlight={
											o.manualInterventionPerformed === 1 ||
											o.manualInterventionPerformed === 5 ||
											o.manualInterventionPerformed === 3
										}
									>
										{o.amount}
									</CurrencyCell>
									<Cell percent underline={i === a.length - 1}>
										{o.percent}
									</Cell>
									<Cell />
									<Cell />
									<Cell />
								</TableRow>
							))}
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow>
							<Cell header align="left">
								Total Income
							</Cell>
							<Cell />
							<CurrencyCell
								{...currency}
								underline
								hideSymbol
								highlight={
									!!data.totalIncomeAmountManualIntervention &&
									data.totalIncomeAmountManualIntervention === 1
								}
							>
								{data.totalIncomeAmount}
							</CurrencyCell>
							<Cell percent underline>
								{data.totalIncomePercentOfSales}
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow>
							<Cell header colSpan={7} align="left">
								Cost of Goods Sold
							</Cell>
						</TableRow>
						{costOfGoodsSold.map((c) => (
							<TableRow
								key={`${c.expenseSection}:${c.expenseCategory}:${c.expenseDescription}`}
								className="striped"
							>
								<Cell indent>{c.expenseDescription}</Cell>
								<Cell>
									{c.mddGrouping}
									{(c.manualInterventionPerformed === 2 ||
										c.manualInterventionPerformed === 3) && (
										<HasMi lowerCategorizationText={lowerCategorizationText} />
									)}{' '}
									{(c.manualInterventionPerformed === 4 ||
										c.manualInterventionPerformed === 5) && (
										<HasMi4Or5
											lowerCategorizationText={lowerCategorizationText}
										/>
									)}{' '}
								</Cell>
								<CurrencyCell
									{...currency}
									hideSymbol
									highlight={
										c.manualInterventionPerformed === 1 ||
										c.manualInterventionPerformed === 5 ||
										c.manualInterventionPerformed === 3
									}
								>
									{c.amount}
								</CurrencyCell>
								<Cell percent>{c.percent}</Cell>
								{c.averageIsPercent ?
									<Cell percent>{c.averageSavedCost}</Cell>
								:	<CurrencyCell {...currency} noZero>
										{c.averageSavedCost}
									</CurrencyCell>
								}
								<Cell>
									<Footnote>{c.footnoteLetter}</Footnote>
								</Cell>
								<CurrencyCell {...currency} hideSymbol noZero>
									{c.biLoss}
								</CurrencyCell>
							</TableRow>
						))}
						<TableRow>
							<Cell colSpan={2}>Variable Cost of Goods</Cell>
							<CurrencyCell {...currency} hideSymbol overline>
								{data.variableCostOfGoodsSoldAmount}
							</CurrencyCell>
							<Cell percent overline>
								{data.variableCostOfGoodsSoldPercentOfSales}
							</Cell>
							{/* Percent Saved is the same as Percent Of Sales */}
							<Cell percent>{data.variableCostOfGoodsSoldPercentOfSales}</Cell>
							<Cell />
							<CurrencyCell {...currency}>
								{data.variableCostOfGoodsSoldBiLoss}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{labour.length > 0 && (
							<TableRow>
								<Cell header colSpan={7} align="left">
									Labour
								</Cell>
							</TableRow>
						)}
						{labour.map((l) => (
							<TableRow
								key={`${l.expenseSection}:${l.expenseCategory}:${l.expenseDescription}`}
								className="striped"
							>
								<Cell indent>{l.expenseDescription}</Cell>
								<Cell>
									{l.mddGrouping}
									{(l.manualInterventionPerformed === 2 ||
										l.manualInterventionPerformed === 3) && (
										<HasMi lowerCategorizationText={lowerCategorizationText} />
									)}{' '}
									{(l.manualInterventionPerformed === 4 ||
										l.manualInterventionPerformed === 5) && (
										<HasMi4Or5
											lowerCategorizationText={lowerCategorizationText}
										/>
									)}{' '}
								</Cell>
								<CurrencyCell
									{...currency}
									hideSymbol
									highlight={
										l.manualInterventionPerformed === 1 ||
										l.manualInterventionPerformed === 5 ||
										l.manualInterventionPerformed === 3
									}
								>
									{l.amount}
								</CurrencyCell>
								<Cell percent>{l.percent}</Cell>
								{l.averageIsPercent ?
									<Cell percent>{l.averageSavedCost}</Cell>
								:	<CurrencyCell {...currency} noZero>
										{l.averageSavedCost}
									</CurrencyCell>
								}
								<Cell>
									<Footnote>{l.footnoteLetter}</Footnote>
								</Cell>
								<CurrencyCell {...currency} hideSymbol noZero>
									{l.biLoss}
								</CurrencyCell>
							</TableRow>
						))}
						{otherCosts.length > 0 && (
							<>
								<TableRow>
									<Cell spacer />
								</TableRow>
								<TableRow>
									<Cell header colSpan={7} align="left">
										Other Costs
									</Cell>
								</TableRow>
							</>
						)}
						{otherCosts.map((o) => (
							<TableRow
								key={`${o.expenseSection}:${o.expenseCategory}:${o.expenseDescription}`}
								className="striped"
							>
								<Cell indent>{o.expenseDescription}</Cell>
								<Cell>
									{o.mddGrouping}
									{(o.manualInterventionPerformed === 2 ||
										o.manualInterventionPerformed === 3) && (
										<HasMi lowerCategorizationText={lowerCategorizationText} />
									)}{' '}
									{(o.manualInterventionPerformed === 4 ||
										o.manualInterventionPerformed === 5) && (
										<HasMi4Or5
											lowerCategorizationText={lowerCategorizationText}
										/>
									)}{' '}
								</Cell>
								<CurrencyCell
									{...currency}
									hideSymbol
									highlight={
										o.manualInterventionPerformed === 1 ||
										o.manualInterventionPerformed === 5 ||
										o.manualInterventionPerformed === 3
									}
								>
									{o.amount}
								</CurrencyCell>
								<Cell percent>{o.percent}</Cell>
								{o.averageIsPercent ?
									<Cell percent>{o.averageSavedCost}</Cell>
								:	<CurrencyCell {...currency} noZero>
										{o.averageSavedCost}
									</CurrencyCell>
								}
								<Cell>
									<Footnote>{o.footnoteLetter}</Footnote>
								</Cell>
								<CurrencyCell {...currency} hideSymbol noZero>
									{o.biLoss}
								</CurrencyCell>
							</TableRow>
						))}
						<TableRow>
							<Cell colSpan={2}>Total Cost of Goods Sold</Cell>
							<CurrencyCell
								{...currency}
								underline
								overline
								hideSymbol
								highlight={
									!!data.totalCostOfGoodsSoldAmountManualIntervention &&
									data.totalCostOfGoodsSoldAmountManualIntervention === 1
								}
							>
								{data.totalCostOfGoodsSoldAmount}
							</CurrencyCell>
							<Cell percent underline overline>
								{data.totalCostOfGoodsSoldPercentOfSales}
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell header colSpan={2} align="left">
								Gross Margin
							</Cell>
							<CurrencyCell {...currency} underline hideSymbol>
								{data.grossMarginAmount}
							</CurrencyCell>
							<Cell percent underline>
								{data.grossMarginPercentOfSales}
							</Cell>
							<Cell />
							<Cell />
							<Cell />
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{expenses.length > 0 && (
							<TableRow>
								<Cell header colSpan={7} align="left">
									Expenses
								</Cell>
							</TableRow>
						)}
						{expenses.map((e) => (
							<TableRow
								key={`${e.expenseSection}:${e.expenseCategory}:${e.expenseDescription}`}
								className="striped"
							>
								<Cell indent>{e.expenseDescription}</Cell>
								<Cell>
									{e.mddGrouping}
									{(e.manualInterventionPerformed === 2 ||
										e.manualInterventionPerformed === 3) && (
										<HasMi lowerCategorizationText={lowerCategorizationText} />
									)}{' '}
									{(e.manualInterventionPerformed === 4 ||
										e.manualInterventionPerformed === 5) && (
										<HasMi4Or5
											lowerCategorizationText={lowerCategorizationText}
										/>
									)}{' '}
								</Cell>
								<CurrencyCell
									{...currency}
									hideSymbol
									highlight={
										e.manualInterventionPerformed === 1 ||
										e.manualInterventionPerformed === 5 ||
										e.manualInterventionPerformed === 3
									}
								>
									{e.amount}
								</CurrencyCell>
								<Cell percent>{e.percent}</Cell>
								{e.averageIsPercent ?
									<Cell percent>{e.averageSavedCost}</Cell>
								:	<CurrencyCell {...currency} noZero>
										{e.averageSavedCost}
									</CurrencyCell>
								}
								<Cell>
									<Footnote>{e.footnoteLetter}</Footnote>
								</Cell>
								<CurrencyCell {...currency} hideSymbol noZero>
									{e.biLoss}
								</CurrencyCell>
							</TableRow>
						))}
						<TableRow>
							<Cell colSpan={2}>Total Expenses</Cell>
							<CurrencyCell
								{...currency}
								underline
								overline
								hideSymbol
								highlight={
									!!data.totalExpensesAmountManualIntervention &&
									data.totalExpensesAmountManualIntervention === 1
								}
							>
								{data.totalExpensesAmount}
							</CurrencyCell>
							<Cell percent underline overline>
								{data.totalExpensesPercentOfSales}
							</Cell>
							<Cell />
							<Cell />
							<Cell />
						</TableRow>
						<TableRow>
							<Cell spacer colSpan={6} />
							<Cell spacer />
						</TableRow>
						<TableRow>
							<Cell header colSpan={2} align="left">
								Net Income / (Loss)
							</Cell>
							<CurrencyCell
								{...currency}
								underline="double"
								highlight={
									!!data.netIncomeAmountManualIntervention &&
									data.netIncomeAmountManualIntervention === 1
								}
							>
								{data.netIncomeAmount}
							</CurrencyCell>
							<Cell percent underline="double">
								{data.netIncomePercentOfSales}
							</Cell>
							<Cell />
							<Cell align="right">Saved Expenses</Cell>
							<CurrencyCell {...currency} underline overline hideSymbol>
								{data.savedExpenses}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow>
							<Cell colSpan={5} />
							<Cell align="right">BI Loss</Cell>
							<CurrencyCell {...currency} underline="double">
								{data.biLoss}
							</CurrencyCell>
						</TableRow>
					</TableBody>
				</CalculationTable>
				{(footnotes.length > 0 ||
					hasMiValuePerformed ||
					hasMiCategoryPerformed ||
					hasMiType4Or5Performed) && (
					<Box>
						<Typography
							variant="body2"
							sx={{
								padding: '6px 8px 6px 12px',
							}}
						>
							Notes
						</Typography>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'auto auto 1fr',
								columnGap: 1,
							}}
						>
							{footnotes.map((f, i) => (
								<Box
									key={`${f.expenseSection}:${f.expenseCategory}`}
									sx={{
										display: 'grid',
										gridTemplateColumns: 'subgrid',
										gridColumn: '1 / -1',
										p: '1px',
										'& a.footnote': {
											padding: '6px 8px 6px 28px',
											display: 'grid',
											gridTemplateColumns: 'subgrid',
											gridColumn: '1 / -1',
											width: '100%',
											textDecoration: 'none',
											'&.active': {
												outline: '1px solid',
												outlineColor: 'accent.main',
												borderRadius: 1,
												backgroundColor: 'offwhite.light',
											},
											'& p': {
												display: 'flex',
												gap: 1,
											},
										},
									}}
									id={'footnote-' + getFootnoteLetter(i)}
								>
									<Footnote
										footnoteLetter={getFootnoteLetter(i)}
										className="footnote"
									>
										<Typography variant="body2">
											{getFootnoteLetter(i)}
										</Typography>
										<Typography variant="body2">-</Typography>
										<Typography variant="body2">{f.note}</Typography>
									</Footnote>
								</Box>
							))}
							{hasMiValuePerformed && (
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: 'subgrid',
										gridColumn: '1 / -1',
										color: 'grey.700',
										padding: '6px 8px 6px 24px',
										width: '100%',
									}}
								>
									<Typography variant="body2" sx={{ justifySelf: 'center' }}>
										<FontAwesomeIcon
											icon={faSquare1}
											size="lg"
											color={theme.palette.highlight.main}
										/>
									</Typography>
									<Typography variant="body2">-</Typography>
									<Typography variant="body2">
										Value has been manually updated
									</Typography>
								</Box>
							)}
							{hasMiCategoryPerformed && (
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: 'subgrid',
										gridColumn: '1 / -1',
										color: 'grey.700',
										padding: '6px 8px 6px 24px',
										width: '100%',
									}}
								>
									<Typography variant="body2" sx={{ justifySelf: 'center' }}>
										<FontAwesomeIcon
											icon={faStarOfLife}
											size="2xs"
											color={theme.palette.highlight.main}
										/>
									</Typography>
									<Typography variant="body2">-</Typography>
									<Typography variant="body2">
										{categorizationText} has been manually updated
									</Typography>
								</Box>
							)}
							{hasMiType4Or5Performed && (
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: 'subgrid',
										gridColumn: '1 / -1',
										color: 'grey.700',
										padding: '6px 8px 6px 24px',
										width: '100%',
									}}
								>
									<Typography variant="body2" sx={{ justifySelf: 'center' }}>
										<FontAwesomeIcon
											icon={faStarOfLife}
											size="xs"
											color={theme.palette.highlight.main}
										/>
										<FontAwesomeIcon
											icon={faStarOfLife}
											size="xs"
											color={theme.palette.highlight.main}
										/>
									</Typography>
									<Typography variant="body2">-</Typography>
									<Typography variant="body2">
										MDDi categorization has been determined using AI.
									</Typography>
								</Box>
							)}
						</Box>
					</Box>
				)}
			</Box>
		);
	},
});
